import { useSeo } from "../lib/seo";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Leaf, Award, Globe2, HeartHandshake, Mountain, ArrowRight } from "lucide-react";
import RALayout from "../components/ra/RALayout";

const VALUES = [
  { icon: Leaf, title: "Explore with a clear conscience", body: "Every journey is fully carbon-offset — so the adventure of a lifetime helps protect the very peaks you came to see." },
  { icon: HeartHandshake, title: "A crew who have your back", body: "Fairly paid, expertly trained and endlessly kind, our guides and porters make the hard days feel easy and the good days magic." },
  { icon: Globe2, title: "Doors most travellers never find", body: "Decades of local friendships open homes, hidden trails and real moments far beyond anything in a guidebook." },
  { icon: Award, title: "Arrive stronger, not just further", body: "Superfood menus and mindful, well-paced days keep you energised at altitude so you savour every single step." },
];

export default function About() {
  useSeo({ title: "About us | Responsible Adventures", description: "Responsible Adventures is a Kathmandu-based, carbon-neutral adventure company founded by Raj Tamang." });
  return (
    <RALayout>
      {/* Intro */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-4">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-moss mb-4">About us</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight leading-[0.98] max-w-3xl" data-testid="about-title">
          Don't just visit the Himalaya — belong to it for a while.
        </h1>
        <p className="mt-6 text-lg text-ink-secondary max-w-2xl leading-relaxed">
          With Responsible Adventures, your bucket-list ambitions become days you'll never forget: sunrise on a
          high pass, laughter around a lodge stove, and the quiet pride of reaching somewhere you never imagined
          you could. Our Nepali team crafts carbon-neutral journeys across Nepal, Bhutan, India and Tibet that are
          as kind to the mountains and their people as they are unforgettable for you. (Tourism Licence 48588/064/065.)
        </p>
      </section>

      {/* Founder spotlight */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-8 items-center rounded-lg overflow-hidden border border-sand-dark bg-white">
          <div className="lg:col-span-2 h-80 lg:h-full min-h-[320px] relative" data-testid="founder-image">
            <img
              src="https://images.unsplash.com/photo-1551632811-561732d1e306?q=80&w=1200&auto=format&fit=crop"
              alt="Raj Tamang, founder" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-3 p-8 sm:p-10">
            <div className="flex items-center gap-2 text-moss mb-3"><Mountain className="w-4 h-4" /><span className="font-mono text-xs uppercase tracking-widest">Founder</span></div>
            <h2 className="font-display font-extrabold text-3xl tracking-tight">Raj Tamang</h2>
            <p className="text-sm text-ink-muted mb-4">Founder & Lead Guide · Responsible Adventures</p>
            <p className="text-ink-secondary leading-relaxed">
              Wherever you're heading, you'll travel in the hands of a globally-recognised Himalayan guide. Raj
              Tamang built Responsible Adventures on a simple promise: that adventure should give back — to you and
              to the mountains. Drawing on decades of guiding across the world's greatest ranges and a reputation
              that reaches deep into the international travel market, Raj personally shapes every itinerary so you
              get world-class logistics wrapped around warm, authentic, community-rooted experiences. It's why
              travellers return again and again — and why partners around the world trust his name.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/tours" data-testid="founder-tours-btn" className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-5 h-11 rounded-sm font-semibold text-sm transition-colors">
                Explore Raj's journeys <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="https://responsibleadventures.com/what-sets-raj-tamang-apart-from-other-trekking-guides-or-adventure-company-owners-in-the-himalayas/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-sand-dark hover:border-moss px-5 h-11 rounded-sm font-medium text-sm transition-colors">
                Read Raj's story
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Why you'll love travelling with us</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div key={v.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="p-6 rounded-md border border-sand-dark bg-white">
                <div className="w-11 h-11 rounded-sm bg-moss-light flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-moss" /></div>
                <h3 className="font-display font-bold tracking-tight mb-1.5">{v.title}</h3>
                <p className="text-sm text-ink-secondary leading-relaxed">{v.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </RALayout>
  );
}
